/**
 * Town-network substrate (HEX-93 placement + HEX-115 topology).
 *
 * Towns cover the world in a guaranteed connected network: every town is within
 * 50 hexes of at least one neighbour, and the first town is within 50 hexes of
 * origin (ADR-0018 §4).
 *
 * Implementation — guaranteed lattice, not probabilistic:
 *
 *   The world is partitioned into `SETTLEMENT_BLOCK_CHUNKS²` chunk blocks.
 *   Every block that passes the origin keep-out has exactly one town, anchored
 *   at a fixed interior cell. This is a GUARANTEE, not a roll: every block
 *   produces a town, so the lattice is dense enough that diagonal neighbours are
 *   ≤48 hexes apart — comfortably within the 50-hex max-gap constraint.
 *
 * Derivation of the ≤48 diagonal bound:
 *
 *   Block span B = SETTLEMENT_BLOCK_CHUNKS × CHUNK_SIZE = 3 × 8 = 24 hexes/axis.
 *   Anchors are spaced 24 hexes per axis; the worst-case pair is diagonal
 *   (Δq = B, Δr = B) with axial distance (B + B + 2B)/2 = 2B = 48 hexes.
 *
 * Determinism without PRNG-stream churn (HEX-83 precedent):
 *   Placement is a pure function of (chunk-coord, seed) via an INDEPENDENT hash,
 *   never the chunk PRNG stream, so non-town tiles stay byte-for-byte identical
 *   to a town-free control.
 *
 * Pillar slots (ADR-0018 §1):
 *   Each town cluster reserves a pillar slot. The slot is left as `ground` at
 *   worldgen time — it materialises as a `pillar` on town liberation (Wave-3).
 *   Wild standalone pillars (HEX-92) are removed; the fast-travel network is
 *   the liberated-town network.
 *
 * Safe-zone easing (ADR-0018 §4):
 *   Towns are boss-held objectives until liberated. Town proximity does NOT ease
 *   `safeZoneDistance`; origin is the sole safe zone until Wave-3 adds easing
 *   for liberated towns.
 */

import { CHUNK_SIZE, type ChunkCoord } from '../core/world';
import { originDistance } from './distance';

/**
 * Edge length (in chunks) of one settlement block. One town per block, anchored
 * at a fixed offset, so the minimum centre-to-centre spacing between orthogonally
 * adjacent towns is `SETTLEMENT_BLOCK_CHUNKS × CHUNK_SIZE` = 24 hexes, and
 * the worst-case (diagonal) pair is 2 × 24 = 48 hexes — within the 50-hex max.
 */
export const SETTLEMENT_BLOCK_CHUNKS = 3;

/**
 * Minimum hex spacing between any two adjacent towns in the lattice. Orthogonal
 * neighbours are exactly 24 hexes apart; diagonal neighbours are 48 hexes.
 */
export const SETTLEMENT_MIN_SPACING = 24;

/**
 * Maximum guaranteed gap (hexes) between any town and its nearest neighbour.
 * Diagonal lattice neighbours in a 24-hex-span grid are always ≤48 hexes apart,
 * so this is the tight upper bound on town-to-town distance.
 */
export const TOWN_NETWORK_MAX_GAP = 48;

/**
 * No frontier town within this axial distance of origin — the spawn town already
 * serves the near field. Measured with raw origin distance so placement never
 * depends on its own output. Set to 14: the nearest possible lattice anchor
 * (distance 13) is excluded; the first included block sits at distance ≈22,
 * satisfying the ADR-0018 §4 "first town ≤50 from origin" rule.
 */
export const SETTLEMENT_MIN_ORIGIN_DISTANCE = 14;

/**
 * Blocks whose anchor falls within this hex distance of origin are probabilistic
 * rather than guaranteed (ADR-0018 §2 density banding). Beyond this distance
 * every block is guaranteed a town. Within it a roll gate keeps the near-origin
 * cluster sparse so the player has to push outward before towns become trivially
 * close.
 *
 * Set to 40: blocks at d ∈ [14, 40) may roll absent; blocks at d ≥ 40 are
 * guaranteed. The nearest guaranteed block (d ≈ 46) satisfies the ≤50 max-gap
 * rule in every scenario: fully absent near-field still leaves a guaranteed
 * ring at d ≈ 46, and any present near-field town has a guaranteed outer
 * neighbour within 24 hexes.
 */
export const TOWN_NEAR_DISTANCE = 40;

/**
 * Probability that a near-origin block (d < TOWN_NEAR_DISTANCE) produces a
 * town. 0.6 ≈ 60 % of near blocks have a town; outer blocks are always 100 %.
 */
export const TOWN_NEAR_RATE = 0.6;

/** Salt for the placement hash; decorrelates from other per-cell hashes. */
const SETTLEMENT_SALT = 0x53_65_74_6c | 0; // "Setl"

/**
 * Fixed anchor offset within a block, in chunk units. The anchor chunk is at
 * offset `ANCHOR_CHUNK_OFFSET` within the 3-chunk block span; the anchor cell
 * is a fixed interior cell so the cluster fits inside one chunk without crossing
 * a chunk edge.
 */
const ANCHOR_CHUNK_OFFSET = Math.floor(SETTLEMENT_BLOCK_CHUNKS / 2); // 1
/** Anchor local cell — interior, room for the 2×2-ish cluster footprint. */
const ANCHOR_LOCAL: readonly [number, number] = [3, 3];

/** FNV-ish small integer hash (mirrors ChunkedWorldGen `mix`). */
function mix(...inputs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of inputs) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}

/** Map a signed 32-bit hash into `[0, 1)`. */
function unitFromHash(h: number): number {
  return (Math.abs(h) % 0x7fffffff) / 0x7fffffff;
}

/** Floor-division that works for negative numerators. */
function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}

/** The settlement anchor a chunk-block produces, in world coords. */
export interface SettlementAnchor {
  /** Town tile world coord — the cluster's interaction centre. */
  readonly q: number;
  readonly r: number;
}

/**
 * The settlement anchor for block `(blockQ, blockR)`, or null if the block is
 * filtered out. Three filters apply in order:
 *
 *  1. Origin keep-out: anchor inside `SETTLEMENT_MIN_ORIGIN_DISTANCE` → null.
 *  2. Near-origin density gate: anchor inside `TOWN_NEAR_DISTANCE` but outside
 *     the keep-out → probabilistic roll, `null` if the block rolls absent
 *     (ADR-0018 §2). The roll is seeded from the block + world seed so it is
 *     deterministic and does not consume any chunk PRNG stream.
 *  3. Outer lattice: guaranteed — always returns an anchor.
 */
function anchorForBlock(blockQ: number, blockR: number, seed: number): SettlementAnchor | null {
  const blockHash = unitFromHash(mix(blockQ, blockR, seed, SETTLEMENT_SALT));

  const anchorCq = blockQ * SETTLEMENT_BLOCK_CHUNKS + ANCHOR_CHUNK_OFFSET;
  const anchorCr = blockR * SETTLEMENT_BLOCK_CHUNKS + ANCHOR_CHUNK_OFFSET;
  const q = anchorCq * CHUNK_SIZE + ANCHOR_LOCAL[0];
  const r = anchorCr * CHUNK_SIZE + ANCHOR_LOCAL[1];
  const d = originDistance(q, r);
  if (d < SETTLEMENT_MIN_ORIGIN_DISTANCE) return null;
  if (d < TOWN_NEAR_DISTANCE && blockHash >= TOWN_NEAR_RATE) return null;
  return { q, r };
}

/**
 * Block index pair (in block coords) for a chunk coord.
 */
function blockOf(cq: number, cr: number): readonly [number, number] {
  return [floorDiv(cq, SETTLEMENT_BLOCK_CHUNKS), floorDiv(cr, SETTLEMENT_BLOCK_CHUNKS)];
}

/**
 * The frontier-town anchor that falls inside chunk `coord`, or null. At most one
 * per chunk: a chunk hosts the anchor only when it is the block's fixed anchor
 * chunk AND the block passes the origin keep-out. Pure + deterministic.
 */
export function settlementAnchorInChunk(coord: ChunkCoord, seed: number): SettlementAnchor | null {
  const [blockQ, blockR] = blockOf(coord.cq, coord.cr);
  const anchor = anchorForBlock(blockQ, blockR, seed);
  if (anchor === null) return null;
  // Only the anchor chunk materialises the cluster — other chunks in the block
  // see the anchor for distance queries but place no tiles.
  const anchorCq = blockQ * SETTLEMENT_BLOCK_CHUNKS + ANCHOR_CHUNK_OFFSET;
  const anchorCr = blockR * SETTLEMENT_BLOCK_CHUNKS + ANCHOR_CHUNK_OFFSET;
  if (coord.cq !== anchorCq || coord.cr !== anchorCr) return null;
  return anchor;
}

/**
 * All frontier-town anchors within `radius` hexes of `(q, r)`. Used internally
 * and by `neighboursWithin50`.
 */
export function nearbySettlementAnchors(
  q: number,
  r: number,
  seed: number,
  radius: number,
): SettlementAnchor[] {
  const blockSpanHexes = SETTLEMENT_BLOCK_CHUNKS * CHUNK_SIZE;
  const cq = floorDiv(q, CHUNK_SIZE);
  const cr = floorDiv(r, CHUNK_SIZE);
  const [centreBlockQ, centreBlockR] = blockOf(cq, cr);
  const reach = Math.ceil(radius / blockSpanHexes) + 1;
  const anchors: SettlementAnchor[] = [];
  for (let bq = centreBlockQ - reach; bq <= centreBlockQ + reach; bq++) {
    for (let br = centreBlockR - reach; br <= centreBlockR + reach; br++) {
      const anchor = anchorForBlock(bq, br, seed);
      if (anchor === null) continue;
      const dq = q - anchor.q;
      const dr = r - anchor.r;
      const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
      if (d <= radius) anchors.push(anchor);
    }
  }
  return anchors;
}

/**
 * All towns within 50 hexes of `(q, r)` — the neighbour query consumed by
 * Wave-3 Evil Pressure (ADR-0018 §5). Pure function of `(q, r, seed)`.
 */
export function neighboursWithin50(q: number, r: number, seed: number): SettlementAnchor[] {
  return nearbySettlementAnchors(q, r, seed, 50);
}
